// Attribution + phone helpers used only by the opt-in ebook preview download.
// They are not part of the WhatsApp `generate_lead` key event, Google Ads
// conversion counting, or a site-wide phone gate.

const STORAGE_KEY = 'dropify_attribution_v1'

// Ad-click ids + UTMs worth persisting from the landing URL. gclid/gbraid/wbraid
// are Google's; fbclid is Meta's; ttclid TikTok; msclkid Microsoft.
const AD_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ttclid',
  'msclkid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

export type Attribution = Record<string, string>

/**
 * Read ad-click ids / UTMs from the current URL and persist them (last-touch
 * wins). Safe to call on every mount; a no-op when nothing relevant is present
 * so ordinary internal navigations never clobber the entry ad click.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    const fresh: Attribution = {}
    for (const key of AD_PARAMS) {
      const value = params.get(key)
      if (value) fresh[key] = value
    }
    if (Object.keys(fresh).length === 0) return

    const stored = getAttribution()
    const merged: Attribution = {
      ...stored,
      ...fresh,
      landing_page: window.location.href,
      captured_at: new Date().toISOString(),
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
  } catch {
    /* storage/URL unavailable — attribution is best-effort */
  }
}

/** Returns the stored attribution object, or {} if none / unavailable. */
export function getAttribution(): Attribution {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Attribution) : {}
  } catch {
    return {}
  }
}

/**
 * Normalise an Indonesian phone number to E.164 (`+628…`). Accepts the common
 * forms buyers type: `08xx`, `8xx`, `62xx`, `+62 8xx`, with spaces/dashes.
 * Returns null if it clearly isn't a valid Indonesian mobile number.
 */
export function normalizePhone(raw: string): string | null {
  const digits = (raw || '').replace(/\D/g, '')
  if (!digits) return null

  let national: string
  if (digits.startsWith('0')) national = `62${digits.slice(1)}`
  else if (digits.startsWith('62')) national = digits
  else if (digits.startsWith('8')) national = `62${digits}`
  else return null

  // Indonesian mobile numbers are 62 + 8 + 8..11 digits.
  if (!/^628[1-9][0-9]{6,11}$/.test(national)) return null
  return `+${national}`
}

/** SHA-256 hex of a string, using the Web Crypto API. Client-only. */
export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input)
  const buffer = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

/** Internal identifier for an ebook-preview opt-in record. */
export function mintClickId(): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${new Date().getTime().toString(36)}`
  return `DR-${uuid.replace(/-/g, '').slice(0, 8).toUpperCase()}`
}
